import { AgGridColumn } from 'ag-grid-react';
import React, { FC } from 'react';
import styled from 'styled-components';
import { Grid } from 'components/atoms/grid';
import { useGrid } from 'components/organisms/timesheets/drawers/calendar-integration/add-activity-page/activity-table';
import { Member } from 'data/members/api';
import { useTranslate } from 'hooks/use-translation';
import {
  getAvatar,
  getPermission,
  getUserName,
  NoResultTeamMemeberEmptyState,
  TeamMemberOptionsRenderer,
} from './table-components';
import {
  memberNameSorter,
  permissionSorter,
  setDeactivedRowStyle,
} from './table-utils';

const GridContainer = styled.div`
  height: calc(100vh - var(--spacing-12));

  .ag-row-selected {
    background-color: var(--blue-20) !important;
  }
`;

const StyledGrid = styled(Grid)`
  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
  }
  .ag-row {
    border-color: var(--border-color);
  }
`;

export const TeamMemberList: FC<{
  memberData: Member[];
  showMemberDetail: (Member) => void;
  openCostRateManager: (id: number) => void;
}> = ({ memberData, showMemberDetail, openCostRateManager }) => {
  const { onGridReady, gridApi } = useGrid();
  const translate = useTranslate();

  const onNameClick = ({ data }) => showMemberDetail(data);

  return (
    <GridContainer className="ag-theme-balham">
      <StyledGrid
        rowData={memberData}
        size="large"
        defaultColDef={{
          filter: false,
          sortable: true,
          resizable: false,
          wrapText: true,
          suppressMovable: true,
          suppressMenu: true,
          editable: false,
          rowDrag: false,
          unSortIcon: true,
        }}
        onFirstDataRendered={() => gridApi?.sizeColumnsToFit()}
        headerHeight={32}
        rowHeight={48}
        getRowStyle={setDeactivedRowStyle}
        suppressContextMenu
        suppressRowClickSelection
        suppressCellSelection
        animateRows
        noRowsOverlayComponent="NoResultTeamMemeberEmptyState"
        frameworkComponents={{
          TeamMemberOptionsRenderer,
          NoResultTeamMemeberEmptyState,
        }}
        getRowNodeId={(d) => d.user.userId}
        getDataPath={(d) => [d.user.userId]}
        onGridReady={onGridReady}
      >
        <AgGridColumn
          width={50}
          headerName=""
          cellRenderer={getAvatar}
          field="user"
          sortable={false}
        />
        <AgGridColumn
          width={200}
          headerName={translate('team_members.table.header.name')}
          cellRenderer={getUserName}
          field="user"
          comparator={memberNameSorter}
          onCellClicked={onNameClick}
          cellRendererParams={{
            translate,
          }}
        />
        <AgGridColumn
          headerName={translate('team_members.table.header.email')}
          width={286}
          field="user.emailId"
        />
        <AgGridColumn
          width={200}
          field="user.role.roleName"
          headerName={translate('team_members.table.header.role')}
        />
        <AgGridColumn
          width={180}
          headerName={translate('team_members.table.header.permission')}
          cellRenderer={getPermission}
          field="permissionSet"
          comparator={(permissionA, permissionB) => {
            return permissionSorter(permissionA, permissionB, translate);
          }}
          cellRendererParams={{
            translate,
          }}
        />
        <AgGridColumn
          width={50}
          sortable={false}
          cellRenderer="TeamMemberOptionsRenderer"
          cellRendererParams={{
            showMemberDetail,
            openCostRateManager,
          }}
        />
      </StyledGrid>
    </GridContainer>
  );
};
